import { EPredicateFields } from '@common/definitions';
import type { Predicate } from '@common/types/objects';
import { predicatesToPayload } from '@utils/predicates';

export type AudienceSearchResultMode = 'employees' | 'communities';

/** Per-community tasks swap the match-count copy only — the search API is unchanged. */
export function resolveAudienceSearchResultI18nKeys(
  mode: AudienceSearchResultMode = 'employees',
) {
  if (mode === 'communities') {
    return {
      result: 'tasks:assignment_audience_search_result',
      placeholder: 'tasks:assignment_audience_search_result_placeholder',
      modalTitle: 'tasks:assignment_audience_modal_title',
    } as const;
  }

  return {
    result: 'common:predicates_filter_search_result',
    placeholder: 'common:predicates_filter_search_result_placeholder',
    modalTitle: 'common:predicates_filter_who_will_receive',
  } as const;
}

/** Employee-scoped filters without a community — count unique communities from search rows. */
export function shouldResolveCommunitiesFromMatchedUsers(
  predicates: Predicate[] | undefined,
): boolean {
  if (hasNonEmptyNetworkPredicate(predicates)) return false;

  const payload = predicatesToPayload(predicates || []);
  const meaningful = payload.filter((predicate) => {
    if (
      predicate.attribute === EPredicateFields.NETWORK ||
      predicate.attribute === EPredicateFields.FUNCTION
    ) {
      return Array.isArray(predicate.value) && predicate.value.length > 0;
    }

    return true;
  });

  if (!meaningful.length) return false;

  return !meaningful.every((predicate) => predicate.attribute === EPredicateFields.FUNCTION);
}

export function collectUniqueCommunityIdsFromUsers(
  users: Array<{ networks?: Array<{ id?: string | number }> }> | undefined,
): Set<string> {
  const ids = new Set<string>();

  users?.forEach((user) => {
    user.networks?.forEach((network) => {
      if (network?.id != null) ids.add(String(network.id));
    });
  });

  return ids;
}

/** Unique communities for matched users — deduped when several users share a community. */
export function resolveCommunitiesFromMatchedUsers<T extends { id: string }>(
  users: Array<{ networks?: T[] }> | undefined,
  organisationCommunities: T[] | undefined,
): T[] {
  const ids = collectUniqueCommunityIdsFromUsers(users);
  if (!ids.size) return [];

  const byId = new Map(
    (organisationCommunities ?? []).map((community) => [String(community.id), community]),
  );
  const fromOrganisation = Array.from(ids)
    .map((id) => byId.get(id))
    .filter(Boolean) as T[];

  if (fromOrganisation.length) return fromOrganisation;

  const seen = new Set<string>();
  const fromUsers: T[] = [];

  users?.forEach((user) => {
    user.networks?.forEach((network) => {
      const id = String(network.id);
      if (!ids.has(id) || seen.has(id)) return;
      seen.add(id);
      fromUsers.push(network);
    });
  });

  return fromUsers;
}

/** The community ids the audience names outright, across every NETWORK predicate. */
function selectedCommunityIds(predicates: Predicate[] | undefined): Set<string> {
  const ids = new Set<string>();

  predicatesToPayload(predicates || [])
    .filter((predicate) => predicate.attribute === EPredicateFields.NETWORK)
    .forEach((predicate) => {
      const values = Array.isArray(predicate.value) ? predicate.value : [predicate.value];
      values.forEach((value) => ids.add(String(value)));
    });

  return ids;
}

/**
 * The communities a per-community team task would open an instance in.
 *
 * Community predicates name them outright, so the selection is the answer; with
 * none the audience is the whole organisation and every community counts. A
 * predicate set matching nobody opens nothing, which is why the employee total
 * still decides between a list and none (ONE-4929).
 *
 * The heading count and the "View all" list both come from here, so the two
 * cannot drift apart.
 */
export function resolveAudienceCommunities<T extends { id: string }>(
  predicates: Predicate[] | undefined,
  organisationCommunities: T[] | undefined,
  matchedEmployeeCount: number,
  matchedUsers?: Array<{ networks?: T[] }> | undefined,
): T[] {
  if (matchedEmployeeCount === 0) return [];

  const communities = organisationCommunities || [];
  const selected = selectedCommunityIds(predicates);
  if (selected.size) {
    return communities.filter((community) => selected.has(String(community.id)));
  }

  if (shouldResolveCommunitiesFromMatchedUsers(predicates)) {
    const fromUsers = resolveCommunitiesFromMatchedUsers(matchedUsers, organisationCommunities);
    if (fromUsers.length) return fromUsers;
  }

  return communities;
}

/** Filters the resolved communities by the modal's search box. */
export function filterAudienceCommunitiesBySearch<T extends { name?: string }>(
  communities: T[],
  search: string | null | undefined,
): T[] {
  const query = search?.trim().toLowerCase();
  if (!query) return communities;

  return communities.filter((community) => community.name?.toLowerCase().includes(query));
}

function hasNonEmptyNetworkPredicate(predicates: Predicate[] | undefined): boolean {
  return predicatesToPayload(predicates || []).some(
    (predicate) => predicate.attribute === EPredicateFields.NETWORK &&
      Array.isArray(predicate.value) &&
      predicate.value.length > 0
  );
}

function hasNonEmptyUserPredicate(predicates: Predicate[] | undefined): boolean {
  return predicatesToPayload(predicates || []).some(
    (predicate) => predicate.attribute === EPredicateFields.USER &&
      Array.isArray(predicate.value) &&
      predicate.value.length > 0
  );
}

/**
 * Empty / broad predicates on `/users/search` can return users outside the
 * communities a network-scoped admin may manage.
 * Callers must pass `allowOrganisationWideAudienceSearch: false` for those
 * roles so we only search when a community (NETWORK) predicate is present,
 * or when specific employees are named (USER).
 */
export function shouldSkipEmptyAudienceUserSearch(
  allowOrganisationWideAudienceSearch: boolean,
  predicates: Predicate[] | undefined
): boolean {
  if (allowOrganisationWideAudienceSearch) return false;
  if (hasNonEmptyNetworkPredicate(predicates)) return false;
  if (hasNonEmptyUserPredicate(predicates)) return false;
  return true;
}

export type AudienceSearchModule =
  | 'event'
  | 'form'
  | 'task'
  | 'organisation_message'
  | 'channel'
  | 'survey'
  | 'pulse_survey'
  | 'course'
  | 'learning_path'
  | 'chatbot'
  | 'performance_review_cycle';

/** With module: audience scope (colleague_scoping=false). Without: keep colleague scoping. */
export function usersSearchColleagueScopingQuery(
  audienceModule?: AudienceSearchModule,
): { colleague_scoping: boolean; module?: AudienceSearchModule } {
  if (!audienceModule) {
    return { colleague_scoping: true };
  }

  return {
    colleague_scoping: false,
    module: audienceModule,
  };
}

/** Employee / admin user list using POST instead of GET. */
export function usersSearchColleagueListQuery(): { colleague_scoping: boolean } {
  return { colleague_scoping: true };
}

/** Admin flows that resolve explicit user ids (chips, hydration). */
export function usersSearchAdminHydrationQuery(
  module: AudienceSearchModule,
): { colleague_scoping: boolean; module: AudienceSearchModule } {
  return { colleague_scoping: false, module };
}

export function emptyAudienceUserSearchResponse(limit = 10) {
  return {
    data: [] as Record<string, unknown>[],
    meta: {
      pagination: {
        total_count: 0,
        offset: 0,
        limit,
      },
    },
  };
}

/** Modal search uses the same filter shape as the inline table when empty. */
export function buildAudienceModalFilter<T extends Record<string, unknown>>(
  audience: T,
  audienceSearch: string | null | undefined,
): T {
  if (!audienceSearch) return audience;

  return { ...audience, search: audienceSearch };
}

export function findUserInAudienceSearchResults(
  users: Array<{ id?: string | number }> | undefined,
  userId: string | number,
): { id?: string | number } | undefined {
  return users?.find((user) => String(user?.id) === String(userId));
}
